/**
 * The frame for every screen shown before there is a session: sign-in, second factor, invitation.
 *
 * No sidebar, no top bar, nothing that implies access. The one thing it carries is the product name and
 * an honest line about what the ERP is — so someone who lands here by accident knows where they are, and
 * someone who belongs here is not shown a navigation they cannot use yet.
 */
import type { ReactNode } from 'react';
import { Panel, cx } from '../ui/primitives';
import { LogoMoment } from '../ui/moments';

export function AuthFrame({
  title, subtitle, children, aside, width = 'max-w-md',
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** A line under the panel: what to do if this is not working. */
  aside?: ReactNode;
  width?: string;
}) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-paper p-6">
      <div className={cx('w-full', width)}>
        <div className="mb-4 flex items-center gap-3">
          <LogoMoment />
          <div>
          <span className="font-mono text-[13px] font-bold tracking-[0.08em] text-brand">RTCOM ERP</span>
          <p className="mt-0.5 text-small text-ink-muted">The RTCOM Business Operating System</p>
          </div>
        </div>

        <Panel>
          <div className="border-b border-line px-5 py-4">
            <h1 className="text-title">{title}</h1>
            {subtitle ? <p className="mt-1 text-body text-ink-muted">{subtitle}</p> : null}
          </div>
          <div className="px-5 py-5">{children}</div>
        </Panel>

        {aside ? <p className="mt-4 text-small text-ink-muted">{aside}</p> : null}
      </div>
    </main>
  );
}

/** A refusal, in the ERP's vocabulary. Never a stack trace, never a bare status code. */
export function AuthError({ error }: { error: unknown }) {
  if (!error) return null;
  const err = error as { code?: string; message?: string };
  const locked = err.code === 'RTC_ACCOUNT_LOCKED';
  return (
    <div
      role="alert"
      className={cx(
        'rounded-[var(--radius-control)] border px-3 py-2 text-small',
        locked ? 'border-admin/30 bg-admin-ground text-admin' : 'border-refused/30 bg-refused-ground text-refused',
      )}
    >
      <p className="font-medium">{locked ? 'This account cannot sign in' : 'Not signed in'}</p>
      <p data-error="auth" className="mt-0.5">{err.message ?? 'Something went wrong.'}</p>
    </div>
  );
}
